import { can } from '@af/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AgentOrb } from '@/components/ui/agent-orb';
import { IconButton } from '@/components/ui/button';
import { IconChat, IconPause, IconPlay } from '@/components/ui/icons';
import { type DotTone, dotTextClass, StatusDot } from '@/components/ui/status-dot';
import { useAgentClick } from '@/lib/agent-select';
import { modelName } from '@/lib/agent-utils';
import { call } from '@/lib/api';
import { relTime, usd } from '@/lib/format';
import { type AgentLive, elapsed, isWaitingRun, type LiveStatus, useNow } from '@/lib/live-agents';
import { useRole } from '@/lib/permissions';
import { qk, useApiMutation } from '@/lib/queries';
import { cn } from '@/lib/utils';
import { AgentHoverCard } from './AgentHoverCard';
import { activityText } from './LiveOverview';

export const LIVE_TONE: Record<LiveStatus, DotTone> = {
  running: 'running',
  approval: 'approval',
  idle: 'idle',
  paused: 'paused',
  failed: 'failed',
};
/** Sort order for the ops view: what needs attention first. */
export const LIVE_ORDER: Record<LiveStatus, number> = {
  approval: 0,
  running: 1,
  failed: 2,
  idle: 3,
  paused: 4,
};

/** Titles for the "current task/run" column: task title, conversation title, or the run mode. */
export function useRunTitles() {
  const role = useRole();
  const tasks = useQuery({
    queryKey: qk.tasks,
    queryFn: () => call('listTasks', { query: { limit: 200 } }),
    enabled: can(role, 'task:read'),
  });
  const convs = useQuery({
    queryKey: qk.conversations(),
    queryFn: () => call('listConversations', { query: {} }),
    enabled: can(role, 'conversation:read'),
  });
  return useMemo(() => {
    const t = new Map((tasks.data ?? []).map((x) => [x.id, x.title]));
    const c = new Map((convs.data ?? []).map((x) => [x.id, x.title]));
    return (run: AgentLive['run']) =>
      run
        ? (run.taskId && t.get(run.taskId)) ||
          (run.conversationId && c.get(run.conversationId)) ||
          `${run.mode} run`
        : null;
  }, [tasks.data, convs.data]);
}

export function LiveStatusLabel({ status, since }: { status: LiveStatus; since?: string | null }) {
  const { t } = useTranslation();
  const tone = LIVE_TONE[status];
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-xs whitespace-nowrap', dotTextClass(tone))}>
      <StatusDot tone={tone} label={t(`ops.status.${status}`)} />
      {t(`ops.status.${status}`)}
      {since ? <span className="font-mono text-faint">· {since}</span> : null}
    </span>
  );
}

function AgentRow({ live, title, now }: { live: AgentLive; title: string | null; now: number }) {
  const { t } = useTranslation();
  const role = useRole();
  const { agent, status, run, snippet, pending } = live;
  const toggle = useApiMutation('updateAgent', { invalidate: [qk.agents] });
  const working = status === 'running' || status === 'approval';
  const since = working && run ? elapsed(run.startedAt ?? run.createdAt, now) : null;
  const paused = agent.status === 'paused';
  const onAgent = useAgentClick(agent.id);
  return (
    <li
      data-testid={`agent-live-${agent.slug}`}
      data-status={status}
      className="grid grid-cols-[auto_1fr_auto] items-center gap-x-3 gap-y-1 border-b border-border px-4 py-3 last:border-b-0 md:grid-cols-[minmax(180px,1.1fr)_170px_minmax(0,2fr)_110px_140px]"
    >
      <AgentHoverCard live={live}>
        <Link
          to="/agents/$id"
          params={{ id: agent.id }}
          onClick={onAgent}
          className="col-span-2 flex min-w-0 items-center gap-2.5 hover:text-accent md:col-span-1"
        >
          <AgentOrb seed={agent.id} size={26} avatar={agent.avatar} name={agent.name} />
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium text-fg">{agent.name}</span>
            <span className="block truncate text-xs text-muted">
              {agent.roleTitle} · <span className="font-mono">{modelName(agent)}</span>
            </span>
          </span>
        </Link>
      </AgentHoverCard>
      <div className="row-start-2 md:row-start-auto">
        <LiveStatusLabel status={status} since={since} />
        {pending.length ? (
          <Link to="/approvals" className="mt-0.5 block text-xs text-warn hover:underline">
            {t('ops.approvalBadge', { count: pending.length })}
          </Link>
        ) : null}
      </div>
      <div className="col-span-3 row-start-3 min-w-0 md:col-span-1 md:row-start-auto">
        {run ? (
          <>
            <div className="truncate text-sm text-fg-2">{title}</div>
            <div
              className={cn(
                'truncate text-xs',
                working && !isWaitingRun(run)
                  ? 'font-mono text-claude'
                  : working
                    ? 'text-muted'
                    : 'text-faint',
              )}
              aria-live={working ? 'polite' : undefined}
            >
              {working
                ? isWaitingRun(run) || !snippet
                  ? activityText(t, live)
                  : snippet
                : t('ops.lastRun', { when: relTime(run.endedAt ?? run.createdAt) })}
            </div>
          </>
        ) : (
          <span className="text-xs text-faint">{t('ops.noRun')}</span>
        )}
      </div>
      <div className="row-start-2 text-right text-xs whitespace-nowrap text-muted md:row-start-auto">
        <div className="font-mono text-fg-2">
          {usd(agent.stats?.costTodayUsd ?? 0, 2)}{' '}
          <span className="font-sans text-muted">{t('ops.spendToday')}</span>
        </div>
        <div>{t('ops.runsToday', { count: agent.stats?.runsToday ?? 0 })}</div>
      </div>
      <div className="col-start-3 row-start-1 flex items-center justify-end gap-0.5 md:col-start-auto md:row-start-auto">
        {can(role, 'conversation:read') ? (
          <Link
            to="/chat"
            search={{ agent: agent.id }}
            aria-label={`${t('ops.chat')} · ${agent.name}`}
            title={t('ops.chat')}
            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-muted hover:bg-hover hover:text-fg"
          >
            <IconChat size={15} />
          </Link>
        ) : null}
        {can(role, 'agent:update') ? (
          <IconButton
            label={paused ? t('ops.resume', { name: agent.name }) : t('ops.pause', { name: agent.name })}
            disabled={toggle.isPending}
            onClick={() =>
              toggle.mutate({ params: { id: agent.id }, body: { status: paused ? 'active' : 'paused' } })
            }
          >
            {paused ? <IconPlay size={14} /> : <IconPause size={14} />}
          </IconButton>
        ) : null}
        {run ? (
          <Link
            to="/runs/$id"
            params={{ id: run.id }}
            className="inline-flex h-7 items-center rounded-md px-2.5 text-xs text-accent hover:bg-hover"
          >
            {t('ops.openRun')}
          </Link>
        ) : null}
      </div>
    </li>
  );
}

/** Live operations list: every agent with status, current run, streaming snippet, spend and actions. */
export function AgentsLiveList({ list }: { list: AgentLive[] }) {
  const titleOf = useRunTitles();
  const anyWorking = list.some((l) => l.status === 'running' || l.status === 'approval');
  const now = useNow(1000, anyWorking);
  return (
    <ul aria-label="Agents" className="overflow-hidden rounded-xl border border-border bg-panel">
      {list.map((l) => (
        <AgentRow key={l.agent.id} live={l} title={titleOf(l.run)} now={now} />
      ))}
    </ul>
  );
}
